import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { getSidebarConfig, SKINNED_SIDEBAR_CONFIGS, type SidebarType } from '../../config/sidebar';
import {
  matchesLayoutRoutes,
  SKIN_CONFIGS,
  type LayoutRoutes,
  type ShellConfig,
} from '../../config/skinRegistry/configs';
import { useBetslipCount } from '../../stores/betslip/selectors';
import useBreakpoints from '../useBreakpoints';
import useDisplayContentConfig from '../useDisplayContentConfig';
import useProductAvailability from '../useProductAvailability';
import { useSkinConfigs } from '../useSkinConfigs';
import { useDrawerNavigationState } from './useDrawerNavigationState';

type RouteShellFlagsParams = {
  isTablet: boolean;
  layout: LayoutRoutes;
  pathname: string;
};

type MainTopInsetParams = {
  headerHeight: number;
  headerHeightMobile?: number;
  headerHeightPublicMobile: number;
  isMobile: boolean;
  isPublicMobile: boolean;
};

type MainBottomInsetParams = {
  betCount: number;
  hasMobileBottomNavInset: boolean;
  hasSportsbookAccess: boolean;
  isMobile: boolean;
  mobileBetslipOffset: number;
  mobileBottomNavHeight: number;
};

const getRouteShellFlags = ({ isTablet, layout, pathname }: RouteShellFlagsParams) => ({
  showSideContent: matchesLayoutRoutes(pathname, layout.sideContentRoutes) && !isTablet,
  showJackpotList: matchesLayoutRoutes(pathname, layout.jackpotRoutes) && pathname !== '/sports',
  isFullWidth: matchesLayoutRoutes(pathname, layout.fullWidthRoutes),
});

const getMainTopInset = ({
  headerHeight,
  headerHeightMobile,
  headerHeightPublicMobile,
  isMobile,
  isPublicMobile,
}: MainTopInsetParams) => {
  const resolvedHeaderHeight = isPublicMobile
    ? headerHeightPublicMobile
    : isMobile
      ? (headerHeightMobile ?? headerHeight)
      : headerHeight;

  return `${resolvedHeaderHeight}px`;
};

const getMainBottomInset = ({
  betCount,
  hasMobileBottomNavInset,
  hasSportsbookAccess,
  isMobile,
  mobileBetslipOffset,
  mobileBottomNavHeight,
}: MainBottomInsetParams) => {
  const makeSpaceForBetSlip = hasSportsbookAccess && betCount > 0 && isMobile;
  const bottomInset = hasMobileBottomNavInset ? mobileBottomNavHeight : 0;
  const betSlipInset =
    makeSpaceForBetSlip && (hasMobileBottomNavInset || !mobileBottomNavHeight) ? mobileBetslipOffset : 0;
  const totalBottomInset = bottomInset + betSlipInset;

  return totalBottomInset ? `${totalBottomInset}px` : 0;
};

const canUseCompactSidebar = ({
  hasSportsbookAccess,
  isMobile,
  shell,
  sidebarType,
}: {
  hasSportsbookAccess: boolean;
  isMobile: boolean;
  shell?: ShellConfig;
  sidebarType: SidebarType | null;
}) => {
  return !isMobile && hasSportsbookAccess && Boolean(sidebarType && shell?.compactSidebarTypes.includes(sidebarType));
};

export const useSportsbookLayoutShell = () => {
  const { pathname } = useLocation();
  const { isDrawerOpen, toggleDrawerVisibility } = useDrawerNavigationState();
  const [isSidebarCompact, setIsSidebarCompact] = useState(false);

  const { isMobile, isTablet } = useBreakpoints();
  const { isPublicMobile } = useDisplayContentConfig();
  const betCount = useBetslipCount();
  const { hasSportsbookAccess } = useProductAvailability();
  const {
    headerHeight,
    headerHeightMobile,
    headerHeightPublicMobile,
    mobileBottomNavHeight = 0,
    mobileBetslipOffset = 36,
    layout,
    shell,
  } = useSkinConfigs(SKIN_CONFIGS);
  const sidebarConfigs = useSkinConfigs(SKINNED_SIDEBAR_CONFIGS);

  const toggleSidebarCompact = useCallback(() => setIsSidebarCompact((current) => !current), []);

  const shellState = useMemo(() => {
    const sidebarType = getSidebarConfig(sidebarConfigs, pathname, isMobile)?.sidebarType ?? null;
    const hasMobileBottomNavInset = isMobile && mobileBottomNavHeight > 0;

    // Gotcha: shell presentation is config-owned. Keep skin-specific shell behavior
    // in SKIN_CONFIGS instead of adding raw skin checks to SportsbookLayout.
    return {
      ...getRouteShellFlags({ isTablet, layout, pathname }),
      mainPaddingTop: getMainTopInset({
        headerHeight,
        headerHeightMobile,
        headerHeightPublicMobile,
        isMobile,
        isPublicMobile,
      }),
      mainPaddingBottom: getMainBottomInset({
        betCount,
        hasMobileBottomNavInset,
        hasSportsbookAccess,
        isMobile,
        mobileBetslipOffset,
        mobileBottomNavHeight,
      }),
      mobileBottomNavInset: hasMobileBottomNavInset ? `${mobileBottomNavHeight}px` : '0px',
      canCompactSidebar: canUseCompactSidebar({ hasSportsbookAccess, isMobile, shell, sidebarType }),
    };
  }, [
    betCount,
    hasSportsbookAccess,
    headerHeight,
    headerHeightMobile,
    headerHeightPublicMobile,
    isMobile,
    isPublicMobile,
    isTablet,
    layout,
    mobileBetslipOffset,
    mobileBottomNavHeight,
    pathname,
    shell,
    sidebarConfigs,
  ]);

  useEffect(() => {
    if (!shellState.canCompactSidebar) {
      setIsSidebarCompact(false);
    }
  }, [shellState.canCompactSidebar]);

  return {
    ...shellState,
    isDrawerOpen,
    isSidebarCompact: shellState.canCompactSidebar && isSidebarCompact,
    toggleDrawerVisibility,
    toggleSidebarCompact,
  };
};
